import { createFileRoute, useParams, useSearch, useNavigate } from '@tanstack/react-router'
import { useUsers, useDeleteUser, useUpdateUser } from '../../../api/queries'
import { Button } from '../../../components/ui/button'
import { Link } from '@tanstack/react-router'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../../components/ui/alert-dialog'
import { AppIcon } from '../../../components/ui/icon'
import { UserAvatar } from '../../../components/UserAvatar'
import { useBreadcrumbs } from '../../../hooks/useBreadcrumbs'
import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useLiveSubscription } from '../../../contexts/LiveEventsContext'
import { PageHeader } from '../../../components/shared/PageHeader'
import { SECTION_ICONS } from '../../../lib/section-icons'
import { EmptyState } from '../../../components/ui/empty-state'
import { mdiAccount, mdiAccountCog, mdiAccountGroup, mdiAccountGroupOutline, mdiCheck, mdiChevronDown, mdiChevronRight, mdiClose, mdiMagnify, mdiPlus, mdiRefresh, mdiTrashCanOutline } from '@mdi/js'

interface UserFilters {
  type?: 'contact' | 'user' | 'user_group' | 'company' | 'all'
  q?: string
  page?: number
  limit?: number
  parent_uid?: string  // "null" for root-only, undefined for all
}

const USER_TYPE_ICONS: Record<string, string> = {
  contact: mdiAccount,
  user: mdiAccountCog,
  user_group: mdiAccountGroup,
  company: SECTION_ICONS.organizations,
}

export const Route = createFileRoute('/$org/users/')({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>): UserFilters => {
    const q = (search.q as string) || ''
    return {
      type: (search.type as 'contact' | 'user' | 'user_group' | 'company' | 'all') || 'all',
      q,
      page: Number(search.page) || 1,
      limit: Number(search.limit) || 20,
      parent_uid: q ? undefined : 'null',
    }
  },
})

function RouteComponent() {
  const { org } = useParams({ from: '/$org/users/' })
  const { type, q: searchQuery, page, limit, parent_uid } = useSearch({ from: '/$org/users/' })
  const navigate = useNavigate({ from: '/$org/users/' })
  const { t } = useTranslation('users')
  const [userToDelete, setUserToDelete] = useState<{ uid: string; name?: string } | null>(null)
  const [editingField, setEditingField] = useState<{ uid: string; field: 'name' | 'email' } | null>(null)
  const [editValue, setEditValue] = useState('')
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set())

  // Live entity notifications: any user row change refreshes this list
  // (and its expanded-children rows, via the manual-key prefix mechanism).
  useLiveSubscription({ entity: 'users' })

  // Set up breadcrumbs
  useBreadcrumbs([
    { title: t('breadcrumb.home'), path: `/${org}` },
    { title: t('breadcrumb.contacts') }
  ])

  // Use React Query hooks
  const {
    data: users = [],
    isLoading,
    error,
    refetch
  } = useUsers(org, {
    type: type === 'all' ? undefined : type,
    q: searchQuery,
    page,
    limit,
    parent_uid: parent_uid,
    include: ['nb_children'],
  })

  const deleteUserMutation = useDeleteUser(org, {
    onSuccess: () => {
      setUserToDelete(null)
    },
  })

  // Update URL search params for filters
  const updateSearch = (updates: Partial<UserFilters>) => {
    const currentSearch = { type, q: searchQuery, page, limit }
    const newSearch = { ...currentSearch, ...updates }

    // Remove default values from URL
    const cleanSearch: any = {}
    if (newSearch.type && newSearch.type !== 'all') cleanSearch.type = newSearch.type
    if (newSearch.q) cleanSearch.q = newSearch.q
    if (newSearch.page && newSearch.page !== 1) cleanSearch.page = newSearch.page
    if (newSearch.limit && newSearch.limit !== 20) cleanSearch.limit = newSearch.limit

    navigate({
      search: cleanSearch,
    })
  }

  const handleTypeChange = (newType: 'contact' | 'user' | 'user_group' | 'company' | 'all') => {
    updateSearch({ type: newType, page: 1 })
  }

  const handleSearchChange = (searchValue: string) => {
    updateSearch({ q: searchValue, page: 1 })
  }

  const handleRefresh = () => {
    refetch()
  }

  const getUserTypeBadge = (userType: string) => {
    const badges: Record<string, string> = {
      user: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
      contact: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
      user_group: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
      company: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
    }
    return badges[userType] || 'bg-muted text-muted-foreground'
  }

  const toggleExpand = useCallback((uid: string) => {
    setExpandedUsers(prev => {
      const next = new Set(prev)
      if (next.has(uid)) {
        next.delete(uid)
      } else {
        next.add(uid)
      }
      return next
    })
  }, [])

  const startEditing = (uid: string, field: 'name' | 'email', currentValue: string) => {
    setEditingField({ uid, field })
    setEditValue(currentValue || '')
  }

  const cancelEditing = () => {
    setEditingField(null)
    setEditValue('')
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={SECTION_ICONS.users}
        title={t('list.title')}
        description={t('list.subtitle')}
        actions={
        <>
          <Link
            to="/$org/users/create"
            params={{ org }}
            search={{ parent_user_uid: undefined }}
          >
            <Button className="bg-primary hover:bg-primary/90" data-testid="create-user-button">
              <AppIcon path={mdiPlus} size="sm" className="mr-1" />
              {t('create.title')}
            </Button>
          </Link>
          <Button onClick={handleRefresh} variant="outline" disabled={isLoading} data-testid="refresh-button">
            <AppIcon path={mdiRefresh} size="sm" className="mr-1" />
            {isLoading ? t('common:status.loading') : t('common:actions.refresh')}
          </Button>
        </>
        }
      />

      {/* Filter Controls */}
      <div className="bg-card rounded-lg border border-border p-4">
        <div className="flex gap-4 items-center flex-wrap">
          {/* Search Input */}
          <div className="flex items-center border border-border rounded-md px-3 py-2 bg-background min-w-64">
            <AppIcon path={mdiMagnify} size="sm" className="mr-2" />
            <input
              type="text"
              placeholder={t('list.search_placeholder')}
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="bg-transparent border-none outline-none text-sm flex-1 text-foreground placeholder:text-muted-foreground"
              data-testid="search-input"
            />
          </div>

          {/* Type Filter */}
          <select
            value={type}
            onChange={(e) => handleTypeChange(e.target.value as 'contact' | 'user' | 'user_group' | 'company' | 'all')}
            className="border border-border rounded-md px-3 py-2 bg-background text-foreground text-sm"
            data-testid="type-filter"
          >
            <option value="all">{t('list.filter_all_types')}</option>
            <option value="user">{t('types.user')}</option>
            <option value="contact">{t('types.contact')}</option>
            <option value="user_group">{t('types.user_group')}</option>
            <option value="company">{t('types.company')}</option>
          </select>

        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4">
          <div className="text-destructive text-sm">
            {(error as any)?.title || (error as any)?.message || t('common:status.error')}
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-card rounded-lg border border-border overflow-x-auto" data-testid="users-table-wrapper">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted/50">
            <tr>
              <th className="w-10 px-3 py-3"></th>
              <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                {t('list.column_name')}
              </th>
              <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                {t('list.column_email')}
              </th>
              <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                {t('list.column_type')}
              </th>
              <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                {t('list.column_parent')}
              </th>
              <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                {t('list.column_created')}
              </th>
              <th className="px-6 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                {t('list.column_actions')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {isLoading ? (
              <tr>
                <td colSpan={7} className="px-6 py-4 text-center text-muted-foreground">
                  {t('list.loading')}
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6">
                  <EmptyState variant="inline" live icon={mdiAccountGroupOutline} title={t('list.empty')} />
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <UserRow
                  key={user.uid}
                  user={user}
                  org={org}
                  depth={0}
                  editingField={editingField}
                  editValue={editValue}
                  setEditValue={setEditValue}
                  startEditing={startEditing}
                  cancelEditing={cancelEditing}
                  getUserTypeBadge={getUserTypeBadge}
                  setUserToDelete={setUserToDelete}
                  expandedUsers={expandedUsers}
                  toggleExpand={toggleExpand}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Users Count */}
      <div className="flex justify-between items-center text-sm text-muted-foreground">
        {users.length > 0 && (
          <p>{t('list.count', { count: users.length })}</p>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={userToDelete !== null} onOpenChange={(open) => !open && setUserToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('detail.delete_dialog_title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('detail.delete_dialog_description', { name: userToDelete?.name || '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setUserToDelete(null)} data-testid="cancel-delete-dialog">{t('common:actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => userToDelete && deleteUserMutation.mutate(userToDelete.uid)}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-delete-dialog"
            >
              {t('common:actions.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

function UserRow({
  user,
  org,
  depth,
  editingField,
  editValue,
  setEditValue,
  startEditing,
  cancelEditing,
  getUserTypeBadge,
  setUserToDelete,
  expandedUsers,
  toggleExpand,
}: {
  user: any
  org: string
  depth: number
  editingField: { uid: string; field: 'name' | 'email' } | null
  editValue: string
  setEditValue: (v: string) => void
  startEditing: (uid: string, field: 'name' | 'email', value: string) => void
  cancelEditing: () => void
  getUserTypeBadge: (type: string) => string
  setUserToDelete: (u: { uid: string; name?: string } | null) => void
  expandedUsers: Set<string>
  toggleExpand: (uid: string) => void
}) {
  const { t } = useTranslation('users')
  const isExpanded = expandedUsers.has(user.uid)
  const hasChildren = (user.nb_children ?? 0) > 0

  // Only fetch children when row is expanded (pass null to disable query)
  const { data: expandedChildren = [] } = useUsers(
    org,
    isExpanded ? { parent_uid: user.uid, include: ['nb_children'] } : null,
  )

  const updateUserMutation = useUpdateUser(org, user.uid)

  const handleSaveEdit = () => {
    if (!editingField) return
    const updateData: any = {}
    updateData[editingField.field] = editValue
    updateUserMutation.mutate(updateData)
    cancelEditing()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveEdit()
    } else if (e.key === 'Escape') {
      cancelEditing()
    }
  }

  const typeIcon = USER_TYPE_ICONS[user.type] || mdiAccount

  return (
    <>
      <tr className="hover:bg-muted/50">
        {/* Expand toggle */}
        <td className="px-3 py-4 whitespace-nowrap">
          {hasChildren ? (
            <button
              onClick={() => toggleExpand(user.uid)}
              className="p-1 hover:bg-muted rounded"
              data-testid={`expand-user-${user.uid}`}
              style={{ marginLeft: `${depth * 16}px` }}
            >
              {isExpanded ? (
                <AppIcon path={mdiChevronDown} size="sm" tone="muted" />
              ) : (
                <AppIcon path={mdiChevronRight} size="sm" tone="muted" />
              )}
            </button>
          ) : (
            <div style={{ marginLeft: `${depth * 16}px`, width: 24 }} />
          )}
        </td>
        {/* Name */}
        <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground" style={{ paddingLeft: `${24 + depth * 16}px` }}>
          <div className="flex items-center gap-2">
          <UserAvatar
            org={org}
            userUid={user.uid}
            hasPicture={!!user.picture?.document_uid}
            name={user.name}
          />
          {editingField?.uid === user.uid && editingField?.field === 'name' ? (
            <div className="flex items-center gap-1">
              <input
                type="text"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={handleKeyDown}
                autoFocus
                className="border border-border rounded px-2 py-1 text-sm bg-background text-foreground w-40"
                data-testid={`edit-name-input-${user.uid}`}
              />
              <button onClick={handleSaveEdit} className="p-1 hover:bg-muted rounded text-green-600" data-testid={`save-name-${user.uid}`}>
                <AppIcon path={mdiCheck} size="xs" />
              </button>
              <button onClick={cancelEditing} className="p-1 hover:bg-muted rounded text-muted-foreground" data-testid={`cancel-name-${user.uid}`}>
                <AppIcon path={mdiClose} size="xs" />
              </button>
            </div>
          ) : (
            <span
              className="cursor-pointer hover:underline"
              onClick={() => startEditing(user.uid, 'name', user.name || '')}
              data-testid={`user-name-${user.uid}`}
            >
              <Link
                to="/$org/users/$userUid"
                params={{ org, userUid: user.uid }}
                className="text-primary hover:text-primary/80 hover:underline"
                data-testid={`user-link-${user.uid}`}
                onClick={(e) => e.stopPropagation()}
              >
                {user.name || '-'}
              </Link>
            </span>
          )}
          </div>
        </td>
        {/* Email */}
        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
          {editingField?.uid === user.uid && editingField?.field === 'email' ? (
            <div className="flex items-center gap-1">
              <input
                type="text"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={handleKeyDown}
                autoFocus
                className="border border-border rounded px-2 py-1 text-sm bg-background text-foreground w-48"
                data-testid={`edit-email-input-${user.uid}`}
              />
              <button onClick={handleSaveEdit} className="p-1 hover:bg-muted rounded text-green-600" data-testid={`save-email-${user.uid}`}>
                <AppIcon path={mdiCheck} size="xs" />
              </button>
              <button onClick={cancelEditing} className="p-1 hover:bg-muted rounded text-muted-foreground" data-testid={`cancel-email-${user.uid}`}>
                <AppIcon path={mdiClose} size="xs" />
              </button>
            </div>
          ) : (
            <span
              className="cursor-pointer hover:underline"
              onClick={() => startEditing(user.uid, 'email', user.email || '')}
              data-testid={`user-email-${user.uid}`}
            >
              {user.email || '-'}
            </span>
          )}
        </td>
        {/* Type */}
        <td className="px-6 py-4 whitespace-nowrap">
          <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${getUserTypeBadge(user.type)}`} data-testid={`user-type-${user.uid}`}>
            <AppIcon path={typeIcon} size="xs" />
            {t(`types.${user.type}` as any, { defaultValue: user.type })}
          </span>
        </td>
        {/* Parent */}
        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
          {user.parent_user_uid ? (
            <Link
              to="/$org/users/$userUid"
              params={{ org, userUid: user.parent_user_uid }}
              className="text-primary hover:text-primary/80 hover:underline"
              data-testid={`parent-link-${user.uid}`}
            >
              {user.parent_user_name || t('list.view_parent')}
            </Link>
          ) : (
            '-'
          )}
        </td>
        {/* Created */}
        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
          {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
        </td>
        {/* Actions */}
        <td className="px-6 py-4 whitespace-nowrap text-sm">
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive/80"
              onClick={() => setUserToDelete({ uid: user.uid, name: user.name })}
              disabled={hasChildren}
              title={hasChildren ? t('list.cannot_delete_has_children') : undefined}
              data-testid={`delete-user-${user.uid}`}
            >
              <AppIcon path={mdiTrashCanOutline} size="sm" />
            </Button>
          </div>
        </td>
      </tr>
      {/* Expanded children */}
      {isExpanded && expandedChildren.map((child: any) => (
        <UserRow
          key={child.uid}
          user={child}
          org={org}
          depth={depth + 1}
          editingField={editingField}
          editValue={editValue}
          setEditValue={setEditValue}
          startEditing={startEditing}
          cancelEditing={cancelEditing}
          getUserTypeBadge={getUserTypeBadge}
          setUserToDelete={setUserToDelete}
          expandedUsers={expandedUsers}
          toggleExpand={toggleExpand}
        />
      ))}
    </>
  )
}
